// Global
import React from 'react';

// Local
import { SearchResults } from './SearchResults';
import { Content } from '@generated/Content/SearchResults.model';
import {
  ComponentParams,
  ComponentRendering,
  GetComponentServerProps,
  Item,
  LayoutServiceData,
} from '@sitecore-content-sdk/nextjs';
import { Themes } from 'lib/themes';
import { FacetConfig } from 'components/authorable/shared/card-listing/ContentGrid/FacetConfigContext';
import { ImageAspectRatio } from 'components/helpers/Atoms/EnhancedNextImage/ImageCommon';
import { PreambleOrientation } from 'components/helpers/Molecules/Preamble/Preamble';
import { ContentGridCardAlignment } from 'components/authorable/shared/card-listing/ContentGrid/ContentGrid';
import { getEnumValue } from 'lib/utils/sitecore/enum';
import { RenderingParamBoolean } from 'lib/types/sitecore';
import graphqlClientFactory from 'lib/graphql-client-factory';
import {
  ContentListWithOverrides,
  getContentListWithOverrides,
} from 'lib/utils/components/content-list-data';
import { getSiteRoot } from 'lib/gql/getSiteRoot';
import {
  CardFields,
  DEFAULT_CONTENT_PAGE_SIZE,
  fieldTypes,
  resolveContentPageSize,
} from 'components/authorable/shared/card-listing/CardListingShared';
import { getGlobalData } from 'lib/hooks/sitecore/useGlobalData';
import { normalizeGuid } from 'lib/utils/string-utils';
import SearchConfigurationQuery, {
  FacetGroupQuery,
  FacetGroupQueryResult,
  SearchConfigurationQueryResult,
} from 'components/authorable/shared/card-listing/ContentGrid/SearchConfiguration.graphql';

export interface AcgStaticConfig {
  staticProps: ContentListWithOverrides | null;
  searchFacetConfig: FacetConfig | null;
  contentListItem: Item | null;
  maxCards?: number;
  fieldsToShow: CardFields[];
  columns: '1' | '2' | '3' | '4';
  gridAspectRatio?: ImageAspectRatio;
  preambleOrientation?: PreambleOrientation;
  cardAlignment?: ContentGridCardAlignment;
  showFacets: boolean;
  showPagination: boolean;
  showResultCount: boolean;
  showSorting: boolean;
  centerLastRow: boolean;
  constrainedComponentWidth: boolean;
  sortByOptions: Item[] | null;
  sortByDirections: Item[] | null;
}

type SearchResultsParams = ComponentParams & {
  cardDetails?: {
    fieldsToShow?: Item[];
  };
  gridAspectRatio?: Item;
  preambleOrientation?: PreambleOrientation;
  cardAlignment?: ContentGridCardAlignment;
  numColumns?: '1' | '2' | '3' | '4';
  maxCards?: string;
  centerLastRow?: RenderingParamBoolean;
  constrainedComponentWidth?: RenderingParamBoolean;
  showFacets?: RenderingParamBoolean;
  showSorting?: RenderingParamBoolean;
  showPagination?: RenderingParamBoolean;
  showResultCount?: RenderingParamBoolean;
};

type SearchResultsProps = Content.SearchResults.SearchResults_Component & {
  params?: SearchResultsParams;
  acgConfig?: AcgStaticConfig;
};

export const Default = (props: SearchResultsProps) => {
  const theme = (props.params?.theme as Themes) || undefined;

  const gridAspectRatio = getEnumValue<ImageAspectRatio>(props.params?.gridAspectRatio);
  const preambleOrientation = props.params?.preambleOrientation;
  const cardAlignment = props.params?.cardAlignment;
  const fieldsToShow = props.params?.cardDetails?.fieldsToShow?.map((x) => x.name as CardFields);
  const maxCards = resolveContentPageSize(
    parsePositiveInteger(props.params?.maxCards),
    props.acgConfig?.maxCards
  );

  // Fallback config when acgConfig is not available (e.g. datasource not yet configured).
  const defaultConfig: AcgStaticConfig = {
    staticProps: null,
    searchFacetConfig: null,
    contentListItem: null,
    maxCards: DEFAULT_CONTENT_PAGE_SIZE,
    fieldsToShow: [...fieldTypes],
    columns: '3',
    showFacets: false,
    showPagination: true,
    showResultCount: true,
    showSorting: true,
    centerLastRow: false,
    constrainedComponentWidth: false,
    sortByOptions: null,
    sortByDirections: null,
  };

  const baseConfig = props.acgConfig ?? defaultConfig;
  const acgConfig: AcgStaticConfig = {
    ...baseConfig,
    showFacets: props.params?.showFacets === '1',
    showPagination: props.params?.showPagination === '1',
    showResultCount: props.params?.showResultCount === '1',
    showSorting: props.params?.showSorting === '1',
    columns: (props.params?.numColumns as '1' | '2' | '3' | '4') || baseConfig.columns,
    maxCards: maxCards ?? baseConfig.maxCards,
    centerLastRow: props.params?.centerLastRow === '1',
    constrainedComponentWidth: props.params?.constrainedComponentWidth === '1',
    gridAspectRatio: gridAspectRatio ?? baseConfig.gridAspectRatio,
    preambleOrientation: preambleOrientation ?? baseConfig.preambleOrientation,
    cardAlignment: cardAlignment ?? baseConfig.cardAlignment,
    fieldsToShow: fieldsToShow ?? baseConfig.fieldsToShow,
  };

  return <SearchResults theme={theme} acgConfig={acgConfig} />;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const parsePositiveInteger = (value: unknown): number | undefined => {
  if (typeof value === 'number') {
    return value > 0 ? value : undefined;
  }

  if (typeof value === 'string') {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) || parsed <= 0 ? undefined : parsed;
  }

  return undefined;
};

// ---------------------------------------------------------------------------
// GraphQL queries
// ---------------------------------------------------------------------------

const AcgDatasourceQuery = /** GQL */ `query AcgDatasourceQuery($path: String!, $language: String!) {
  item(path: $path, language: $language) {
    id
    name
    contentList: field(name: "contentList") { jsonValue }
    searchConfiguration: field(name: "searchConfiguration") { jsonValue }
    legacySearchConfiguration: field(name: "searchConfiguaration") { jsonValue }
    sortByOptions: field(name: "sortByOptions") { jsonValue }
    sortByDirections: field(name: "sortByDirections") { jsonValue }
  }
}`;

const ContentListItemQuery = /** GQL */ `query ContentListItemQuery($path: String!, $language: String!) {
  item(path: $path, language: $language) {
    id
    name
    Items: field(name: "Items") { jsonValue }
    MetadataServiceTags: field(name: "MetadataServiceTags") { jsonValue }
    contentType: field(name: "contentType") { jsonValue }
    relatedContent: field(name: "relatedContent") { jsonValue }
    searchTerm: field(name: "searchTerm") { jsonValue }
    defaultSortBy: field(name: "defaultSortBy") { jsonValue }
    defaultSortDirection: field(name: "defaultSortDirection") { jsonValue }
  }
}`;

// ---------------------------------------------------------------------------
// Query result types
// ---------------------------------------------------------------------------

interface AcgDatasourceQueryResult {
  item: {
    id: string;
    name: string;
    contentList?: { jsonValue?: Item | null };
    searchConfiguration?: { jsonValue?: { value?: string } | null };
    legacySearchConfiguration?: { jsonValue?: { value?: string } | null };
    sortByOptions?: { jsonValue?: Item[] | null };
    sortByDirections?: { jsonValue?: Item[] | null };
  } | null;
}

interface ContentListItemQueryResult {
  item: {
    id: string;
    name: string;
    Items?: { jsonValue?: { value?: string } | null };
    MetadataServiceTags?: { jsonValue?: { value?: string } | null };
    contentType?: { jsonValue?: { value?: string } | null };
    relatedContent?: { jsonValue?: { value?: boolean } | null };
    searchTerm?: { jsonValue?: { value?: string } | null };
    defaultSortBy?: { jsonValue?: { value?: string } | null };
    defaultSortDirection?: { jsonValue?: { value?: string } | null };
  } | null;
}

const parseCheckboxValue = (value: unknown): boolean => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    const normalizedValue = value.trim().toLowerCase();
    return normalizedValue === '1' || normalizedValue === 'true' || normalizedValue === 'yes';
  }

  if (typeof value === 'number') {
    return value === 1;
  }

  return false;
};

const buildSearchConfigurationPath = ({
  value,
  siteRootPath,
}: {
  value: string;
  siteRootPath?: string;
}) => {
  const normalizedId = normalizeGuid(value);
  if (normalizedId) {
    return `{${normalizedId}}`;
  }

  if (value.startsWith('/sitecore/')) {
    return value;
  }

  if (!siteRootPath) {
    return undefined;
  }

  return `${siteRootPath}/Data/Search/Search Configurations/${value}`;
};

const mapFacetGroupToConfig = (
  facetGroup: NonNullable<FacetGroupQueryResult['item']>,
  overrideGroupLabel?: string
): FacetConfig['facetGroup'][number] | null => {
  const groupName = facetGroup.groupName?.jsonValue?.value ?? '';
  const groupLabel = overrideGroupLabel || facetGroup.groupLabel?.jsonValue?.value || groupName;
  if (!groupName || !groupLabel) {
    return null;
  }

  const expandedByDefault = facetGroup.expandedByDefault?.jsonValue?.value ?? false;
  const rawThreshold = facetGroup.showMoreThreshold?.jsonValue?.value as string | undefined;
  const parsedThreshold = rawThreshold ? parseInt(rawThreshold, 10) : NaN;
  const sortItemName = facetGroup.sortOrder?.jsonValue?.[0]?.name?.toLowerCase() ?? '';
  const sortOrder = sortItemName.includes('alpha')
    ? 'alphabetical'
    : sortItemName.includes('count')
      ? 'count'
      : undefined;

  const facets = (facetGroup.children?.results ?? [])
    .map((facet) => {
      const facetId = facet.facetId?.jsonValue?.value ?? '';
      const valueLabel = facet.valueLabel?.jsonValue?.value ?? '';
      if (!facetId || !valueLabel) {
        return null;
      }

      const isPinned = parseCheckboxValue(
        facet.isPinned?.jsonValue?.value ?? facet.isPinnedDisplayName?.jsonValue?.value
      );

      return {
        facetId,
        valueLabel,
        ...(isPinned ? { isPinned: true } : {}),
      };
    })
    .filter((facet): facet is { facetId: string; isPinned?: true; valueLabel: string } => !!facet);

  if (facets.length === 0) {
    return null;
  }

  return {
    groupName,
    groupLabel,
    facets,
    expandedByDefault,
    ...(sortOrder ? { sortOrder } : {}),
    ...(!Number.isNaN(parsedThreshold) ? { showMoreThreshold: parsedThreshold } : {}),
  };
};

async function loadFacetConfig({
  searchConfigurationValue,
  language,
  siteRootPath,
}: {
  searchConfigurationValue: string;
  language: string;
  siteRootPath?: string;
}): Promise<{ facetConfig?: FacetConfig }> {
  const path = buildSearchConfigurationPath({ value: searchConfigurationValue, siteRootPath });

  if (!path) {
    return {};
  }

  const client = graphqlClientFactory();
  const { item } = await client.request<SearchConfigurationQueryResult>(SearchConfigurationQuery, {
    path,
    language,
  });

  if (!item) {
    return {};
  }

  const facetGroupRefs = (item.facetGroup?.jsonValue ?? []).filter(
    (
      facetGroup
    ): facetGroup is {
      id?: string;
      name?: string;
    } => !!facetGroup
  );

  // "Facet Group Override Settings" child items let authors override just the
  // display label of a base Facet Group without duplicating its facet values.
  // Each override references a base Facet Group via `facetGroup` and supplies
  // a replacement `groupLabel`. `facetGroup`'s raw `value` is either a GUID
  // (Droplink reference) or plain text (Droplist selection); detect which via
  // `normalizeGuid` and key the override map accordingly.
  const overrideLabelByFacetGroupId = new Map<string, string>();
  const overrideLabelByFacetGroupName = new Map<string, string>();
  (item.children?.results ?? []).forEach((child) => {
    const fields = child.fields ?? [];
    const overrideLabel = fields.find((f) => f.name === 'groupLabel')?.value;
    const refValue = fields.find((f) => f.name === 'facetGroup')?.value;
    if (!overrideLabel || !refValue) {
      return;
    }

    const normalizedId = normalizeGuid(refValue);
    if (normalizedId) {
      overrideLabelByFacetGroupId.set(normalizedId, overrideLabel);
    } else {
      overrideLabelByFacetGroupName.set(refValue.trim().toLowerCase(), overrideLabel);
    }
  });

  // We intentionally ignore `item.facetGroup.targetItems` (the expanded shape
  // returned by `SearchConfigurationQuery`) and always refetch each facet
  // group individually via `FacetGroupQuery`. Reason: when the fragment that
  // selects `children` is applied through `MultilistField.targetItems`,
  // Sitecore Edge multiplies the per-child field cost by the number of
  // targets. Adding an explicit `first:` argument to `children` in that
  // fragment breaches the query-cost budget and the whole query is rejected
  // (facetConfig becomes undefined and no facets render). Per-ref queries
  // each get their own budget, so `children(first: 100)` in `FacetGroupQuery`
  // is safe and returns all facet values (not just the first 10).
  const facetGroups =
    facetGroupRefs.length > 0
      ? (
          await Promise.all(
            facetGroupRefs.map(async (facetGroupRef) => {
              if (!facetGroupRef.id) {
                return undefined;
              }

              const { item: facetGroup } = await client.request<FacetGroupQueryResult>(
                FacetGroupQuery,
                {
                  path: `{${normalizeGuid(facetGroupRef.id) ?? facetGroupRef.id}}`,
                  language,
                }
              );

              return facetGroup;
            })
          )
        ).filter(
          (facetGroup): facetGroup is NonNullable<FacetGroupQueryResult['item']> => !!facetGroup
        )
      : [];

  const mappedFacetGroups = facetGroups
    .map((facetGroup) => {
      const normalizedId = normalizeGuid(facetGroup.id) ?? facetGroup.id;
      const overrideLabel =
        overrideLabelByFacetGroupId.get(normalizedId) ??
        overrideLabelByFacetGroupName.get(facetGroup.name?.trim().toLowerCase() ?? '');
      return mapFacetGroupToConfig(facetGroup, overrideLabel);
    })
    .filter((facetGroup): facetGroup is FacetConfig['facetGroup'][number] => !!facetGroup);

  return {
    facetConfig: mappedFacetGroups.length > 0 ? { facetGroup: mappedFacetGroups } : undefined,
  };
}

// ---------------------------------------------------------------------------
// Resolve the ACG datasource path from the Droplist field value
// ---------------------------------------------------------------------------

async function resolveAcgPath(
  fieldValue: string,
  language: string,
  layoutData: LayoutServiceData
): Promise<string> {
  const normalizedId = normalizeGuid(fieldValue);
  if (normalizedId) return `{${normalizedId}}`;
  if (fieldValue.startsWith('/sitecore/')) return fieldValue;

  const { siteName } = getGlobalData(layoutData?.sitecore?.context);

  const siteRoot = siteName
    ? await getSiteRoot(siteName, language).catch(() => undefined)
    : undefined;

  return siteRoot?.path
    ? `${siteRoot.path}/Data/AdvancedContentGrids/${fieldValue}`
    : `/sitecore/content/GMI/Shared/Data/AdvancedContentGrids/${fieldValue}`;
}

// ---------------------------------------------------------------------------
// Fetch the content-list item with all fields needed by parseContentListItem
// ---------------------------------------------------------------------------

async function fetchContentListItem(
  contentListRef: { id?: string; fields?: Record<string, unknown> } | undefined,
  language: string,
  client: ReturnType<typeof graphqlClientFactory>
): Promise<Item | undefined> {
  if (!contentListRef?.id) {
    return contentListRef?.fields ? (contentListRef as Item) : undefined;
  }

  const id = normalizeGuid(contentListRef.id);
  const path = id ? `{${id}}` : contentListRef.id;

  const { item } = await client.request<ContentListItemQueryResult>(ContentListItemQuery, {
    path,
    language,
  });

  if (!item) return undefined;
  return {
    id: item.id,
    name: item.name,
    fields: {
      Items: item.Items?.jsonValue ?? null,
      MetadataServiceTags: item.MetadataServiceTags?.jsonValue ?? null,
      contentType: item.contentType?.jsonValue ?? null,
      relatedContent: item.relatedContent?.jsonValue ?? null,
      searchTerm: item.searchTerm?.jsonValue ?? null,
      defaultSortBy: item.defaultSortBy?.jsonValue ?? null,
      defaultSortDirection: item.defaultSortDirection?.jsonValue ?? null,
    },
  } as Item;
}

// ---------------------------------------------------------------------------
// Resolve full ACG config from the advancedContentGridPath field value
// ---------------------------------------------------------------------------

async function resolveAcgConfig(
  fieldValue: string,
  language: string,
  layoutData: LayoutServiceData,
  maxCards?: number
): Promise<AcgStaticConfig | null> {
  const resolvedMaxCards = resolveContentPageSize(maxCards);
  const client = graphqlClientFactory();
  const acgPath = await resolveAcgPath(fieldValue, language, layoutData);

  const { item } = await client.request<AcgDatasourceQueryResult>(AcgDatasourceQuery, {
    path: acgPath,
    language,
  });

  if (!item) return null;

  const contentListItem = await fetchContentListItem(
    item.contentList?.jsonValue as { id?: string; fields?: Record<string, unknown> } | undefined,
    language,
    client
  );

  const searchConfigValue = (
    (item.searchConfiguration?.jsonValue as { value?: string } | undefined) ??
    (item.legacySearchConfiguration?.jsonValue as { value?: string } | undefined)
  )?.value;

  // Pre-fetch search results
  let staticProps: ContentListWithOverrides | null = null;
  if (contentListItem) {
    try {
      staticProps = await getContentListWithOverrides(
        {
          componentName: 'AdvancedContentGrid',
          fields: { contentList: contentListItem },
          params: {},
        } as ComponentRendering<{ contentList?: Item }>,
        layoutData,
        { withFacets: true, maxCards: resolvedMaxCards }
      );
    } catch {
      // Static props unavailable — runtime search will still work
    }
  }

  const hasSearchConfiguration = !!searchConfigValue;
  const needsSiteScopedLookup = !!(
    searchConfigValue &&
    !normalizeGuid(searchConfigValue) &&
    !searchConfigValue.startsWith('/sitecore/')
  );
  const { siteName } = getGlobalData(layoutData?.sitecore?.context);
  const siteRoot = needsSiteScopedLookup
    ? await getSiteRoot(siteName, language).catch(() => undefined)
    : undefined;
  const siteRootPath = siteRoot?.path;
  let searchFacetConfig: FacetConfig | undefined;

  if (hasSearchConfiguration) {
    try {
      const result = await loadFacetConfig({
        searchConfigurationValue: searchConfigValue,
        language,
        siteRootPath,
      });
      searchFacetConfig = result.facetConfig;
    } catch {}
  }

  // JSON-serializable — use `null` instead of `undefined`.
  return {
    staticProps,
    contentListItem: contentListItem ?? null,
    maxCards: resolvedMaxCards,
    fieldsToShow: [...fieldTypes],
    columns: '3',
    showFacets: hasSearchConfiguration && !!searchFacetConfig,
    showPagination: true,
    showResultCount: true,
    showSorting: true,
    centerLastRow: false,
    constrainedComponentWidth: false,
    searchFacetConfig: searchFacetConfig ?? null,
    sortByOptions: (item.sortByOptions?.jsonValue as Item[] | null) ?? null,
    sortByDirections: (item.sortByDirections?.jsonValue as Item[] | null) ?? null,
  };
}

// ---------------------------------------------------------------------------
// getStaticProps
// ---------------------------------------------------------------------------

export const getComponentServerProps: GetComponentServerProps = async (rendering, layoutData) => {
  try {
    const ld = layoutData as LayoutServiceData;
    const language = ld?.sitecore?.context?.language ?? 'en';
    const maxCards = resolveContentPageSize(parsePositiveInteger(rendering.params?.maxCards));

    const fieldValue = (
      (rendering.fields as Record<string, unknown> | undefined)?.advancedContentGridPath as
        | { value?: string }
        | undefined
    )?.value;

    if (!fieldValue) return {};

    const acgConfig = await resolveAcgConfig(fieldValue, language, ld, maxCards);
    return acgConfig ? { acgConfig } : {};
  } catch {
    return {};
  }
};
